import React from "react";
import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { TIndicatorReportsConfigForm } from "~/components/Forms/IndicatorReportsConfigForm";
import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import { REPORT_FILTER_FIELD_NAMES } from "~/pages/IndicatorReports/lib";
import {
  Dealer,
  RegMens,
  TypeGroupDealerIndicatorValues,
  useGetDealersCitiesLazyQuery,
  useGetDealersFederalAreaLazyQuery,
  useGetDealersRegionsLazyQuery,
  useGetMyDealersLazyQuery,
  useGetMyHoldingsLazyQuery,
  useGetRegMensLazyQuery,
} from "~/schemas/generated/graphql";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";
import { Field } from "~/shared/ui/Field";

interface IGroupSelectResolverProps {
  control: Control<TIndicatorReportsConfigForm>;
  selectedGroup: TypeGroupDealerIndicatorValues;
}

const GroupSelectResolver = ({
  control,
  selectedGroup,
}: IGroupSelectResolverProps) => {
  const { t } = useTranslation();

  // Dealers for select
  const dealersSelect = useSelectorDataConfigurator<Dealer>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetMyDealersLazyQuery,
    responseDataGetterString: "DealerGetter.getMyDealers.items",
    customValueHandler: ({ DealerName, id }: Dealer) => ({
      label: DealerName,
      value: id,
    }),
    searchConfig: (search) => ({ partName: search }),
  });

  // Holdings for select
  const holdingsSelect = useSelectorDataConfigurator<Dealer>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetMyHoldingsLazyQuery,
    responseDataGetterString: "DealerGetter.getMyHoldings.items",
    customValueHandler: ({ DealerName, id }: Dealer) => ({
      label: DealerName,
      value: id,
    }),
    searchConfig: (search) => ({ partName: search }),
  });

  // RegMans for select
  const regMansSelect = useSelectorDataConfigurator<RegMens>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetRegMensLazyQuery,
    responseDataGetterString: "UserGetter.getRegMens.items",
    customValueHandler: (element) => ({
      label: element.fullname,
      value: element.id,
    }),
    searchConfig: (search) => ({
      searchField: "fullname",
      searchText: search,
    }),
  });

  // Cities for select
  const citiesSelect = useSelectorDataConfigurator<string>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetDealersCitiesLazyQuery,
    responseDataGetterString: "DealerGetter.getDealersCities.items",
    customValueHandler: (element) => ({ label: element, value: element }),
  });

  // Regions for select
  const regionsSelect = useSelectorDataConfigurator<string>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetDealersRegionsLazyQuery,
    responseDataGetterString: "DealerGetter.getDealersRegions.items",
    customValueHandler: (element) => ({ label: element, value: element }),
  });

  // FederalArea for select
  const federalAreaSelect = useSelectorDataConfigurator<string>({
    fetchPolicy: "no-cache",
    queryGenerator: useGetDealersFederalAreaLazyQuery,
    responseDataGetterString: "DealerGetter.getDealersFederalArea.items",
    customValueHandler: (element) => ({ label: element, value: element }),
  });

  // Resolve selectors and add addition data
  const resolveSelectInstance = () => {
    switch (selectedGroup) {
      case TypeGroupDealerIndicatorValues.Dealer:
        return {
          label: t("reports.selectDealers"),
          name: REPORT_FILTER_FIELD_NAMES[
            TypeGroupDealerIndicatorValues.Dealer
          ],
          searchByServer: true,
          ...dealersSelect,
        };
      case TypeGroupDealerIndicatorValues.Holding:
        return {
          label: t("reports.selectHolding"),
          name: REPORT_FILTER_FIELD_NAMES[
            TypeGroupDealerIndicatorValues.Holding
          ],
          searchByServer: true,
          ...holdingsSelect,
        };
      case TypeGroupDealerIndicatorValues.Regmen:
        return {
          label: t("reports.selectRegMen"),
          name: REPORT_FILTER_FIELD_NAMES[
            TypeGroupDealerIndicatorValues.Regmen
          ],
          searchByServer: true,
          ...regMansSelect,
        };
      case TypeGroupDealerIndicatorValues.City:
        return {
          label: t("reports.selectCity"),
          name: REPORT_FILTER_FIELD_NAMES[TypeGroupDealerIndicatorValues.City],
          searchByServer: false,
          ...citiesSelect,
        };
      case TypeGroupDealerIndicatorValues.Region:
        return {
          label: t("reports.selectRegion"),
          name: REPORT_FILTER_FIELD_NAMES[
            TypeGroupDealerIndicatorValues.Region
          ],
          searchByServer: false,
          ...regionsSelect,
        };
      case TypeGroupDealerIndicatorValues.Fo:
        return {
          label: t("reports.selectFO"),
          name: REPORT_FILTER_FIELD_NAMES[TypeGroupDealerIndicatorValues.Fo],
          searchByServer: false,
          ...federalAreaSelect,
        };
    }
  };

  const selectInstance = resolveSelectInstance();

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      selectInstance.introspectionHandler();
    }
  };

  return (
    <Field
      type="select"
      name={selectInstance.name}
      control={control}
      label={selectInstance.label}
      mode="multiple"
      labelInValue
      showSearch
      options={selectInstance.rowsForSelect}
      loading={selectInstance.dataRequestLoading}
      onPopupScroll={handlePopupScroll}
      // Для справочников с searchConfig поиск идёт на сервер,
      // для остальных — локальный фильтр по подписи опции
      {...(selectInstance.searchByServer
        ? { filterOption: false, onSearch: selectInstance.searchValue }
        : {
            filterOption: filterOptionByLabel,
          })}
    />
  );
};

export default GroupSelectResolver;
